import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Discreet "Install Sasha" promo bar that surfaces on Android Chrome/Edge
 * (where the `beforeinstallprompt` event fires) and on iOS Safari (where the
 * event is NOT fired and we must show manual instructions).
 *
 * Hidden:
 *  - When the app is already running standalone (display-mode: standalone)
 *  - When the user dismissed the prompt this session (sessionStorage flag)
 *  - When the user installed the app (`appinstalled` event)
 */
export default function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window.sessionStorage.getItem("jante_install_dismissed") === "1") {
        setDismissed(true);
        return;
      }
    } catch {
      /* storage unavailable */
    }
    // Hide if already installed / running standalone
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setDismissed(true);
      return;
    }
    const ua = window.navigator.userAgent || "";
    const isIos = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    // iOS Safari never fires beforeinstallprompt — show manual hint after 8s.
    if (isIos && isSafari) {
      const t = setTimeout(() => setShowIos(true), 8000);
      return () => clearTimeout(t);
    }
    // Android / desktop Chrome path
    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setShowIos(false);
      setDismissed(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem("jante_install_dismissed", "1");
    } catch {
      /* storage unavailable */
    }
  };

  const installNow = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* user dismissed */
    }
    setDeferredPrompt(null);
  };

  if (dismissed) return null;
  if (!deferredPrompt && !showIos) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60]"
      data-testid="install-pwa-prompt"
    >
      <div className="bg-[var(--emerald)] text-[var(--ivory)] shadow-2xl px-5 py-4 flex items-start gap-3 border-t-4 border-[var(--gold)]">
        <Download size={18} strokeWidth={1.5} className="text-[var(--gold)] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl leading-tight">Install Sasha</div>
          {deferredPrompt ? (
            <p className="text-xs text-[var(--ivory)]/85 mt-1 leading-relaxed">
              Add Sasha to your home screen for instant access — and unlock voice replies on the go.
            </p>
          ) : (
            <p className="text-xs text-[var(--ivory)]/85 mt-1 leading-relaxed">
              On iPhone: tap the <span className="font-semibold">Share</span> icon, then{" "}
              <span className="font-semibold">"Add to Home Screen"</span> for full-screen Sasha &amp; voice.
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            {deferredPrompt && (
              <button
                type="button"
                onClick={installNow}
                className="text-[10px] uppercase tracking-[0.18em] bg-[var(--gold)] text-[var(--emerald)] px-3 py-2 hover:opacity-90"
                data-testid="install-pwa-confirm"
              >
                Install
              </button>
            )}
            <button
              type="button"
              onClick={dismiss}
              className="text-[10px] uppercase tracking-[0.18em] text-[var(--ivory)]/70 hover:text-[var(--ivory)]"
              data-testid="install-pwa-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-[var(--ivory)]/60 hover:text-[var(--ivory)] shrink-0"
          aria-label="Close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
